import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useCampaign } from '../contexts/CampaignContext'
import { useAuth } from '../contexts/AuthContext'
import { useModal } from '../contexts/ModalContext'
import { MODULES, ALWAYS_ON } from '../config/modules'
import SettingsShell from '../components/settings/SettingsShell'
import './CampaignSettingsPage.css'

const SETTINGS_SECTIONS = [
  { id: 'branding', label: 'Branding', icon: '🎨' },
  { id: 'modules', label: 'Modules', icon: '🧩' },
  { id: 'map', label: 'Map', icon: '🗺️' },
  { id: 'members', label: 'Members', icon: '👥' },
]

// Feyblood is the one section that doesn't apply to every campaign — its only
// content is per-user access grants for a module most campaigns don't run — so
// it's appended only when the module is actually enabled here.
const FEYBLOOD_SECTION = { id: 'feyblood', label: 'Feyblood', icon: '🍄' }

function CampaignSettingsPage() {
  // Loading MUST be checked before anything below touches `campaign` or
  // `canEdit` — both start out false/null while CampaignContext is still
  // fetching /api/campaigns, and evaluating the canEdit redirect during that
  // window would permanently bounce a DM off their own settings page.
  const { campaign, canEdit, loading, refreshCampaigns } = useCampaign()
  // Site-admin status (distinct from `canEdit`, which is true for a campaign
  // DM OR a site admin). Member autocomplete is gated on the SITE role only
  // — a DM who isn't also a site admin must never see the full account
  // list, or the server's deliberately vague "can't tell you why that
  // failed" 400 on member-add becomes pointless (they could just enumerate
  // usernames from the dropdown instead).
  const { isAdmin, user, authEpoch } = useAuth()
  const navigate = useNavigate()
  const { alert: showAlert, confirm } = useModal()

  // ---- Branding form state ----
  const [name, setName] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [logoEmoji, setLogoEmoji] = useState('')
  const [savingBranding, setSavingBranding] = useState(false)

  // ---- Module toggle state ----
  const [draftModules, setDraftModules] = useState([])
  const [savingModules, setSavingModules] = useState(false)

  // ---- Map upload state ----
  const [mapFile, setMapFile] = useState(null)
  const [mapPreviewUrl, setMapPreviewUrl] = useState(null)
  const [mapWidth, setMapWidth] = useState('')
  const [mapHeight, setMapHeight] = useState('')
  const [uploadingMap, setUploadingMap] = useState(false)

  // ---- Member management state ----
  const [members, setMembers] = useState([])
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [membersError, setMembersError] = useState(null)
  const [newUsername, setNewUsername] = useState('')
  const [newRole, setNewRole] = useState('player')
  const [addMemberError, setAddMemberError] = useState('')
  const [addingMember, setAddingMember] = useState(false)
  const [removingUserId, setRemovingUserId] = useState(null)

  // ---- Member autocomplete state (site admin only) ----
  const [siteUsers, setSiteUsers] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  // ---- Feyblood access state ----
  const [savingAccessFor, setSavingAccessFor] = useState(null)

  // ---- Section nav ----
  const [activeSection, setActiveSection] = useState('branding')

  // Reads the SAVED module list, not `draftModules` — an unsaved tick in the
  // Modules section must not conjure a settings section for a module the
  // campaign doesn't have yet.
  const feybloodEnabled = Array.isArray(campaign?.enabledModules)
    && campaign.enabledModules.includes('feyblood')

  const sections = feybloodEnabled ? [...SETTINGS_SECTIONS, FEYBLOOD_SECTION] : SETTINGS_SECTIONS

  // Turning the module off while sitting in its section would otherwise leave
  // the nav item gone and the panel blank.
  useEffect(() => {
    if (!feybloodEnabled && activeSection === 'feyblood') setActiveSection('branding')
  }, [feybloodEnabled, activeSection])

  // Resync local form state on initial load and when switching to a
  // *different* campaign (id changes) — but NOT on every refreshCampaigns()
  // triggered by saving one section, which would otherwise stomp unsaved
  // edits sitting in another section's still-mounted form fields (e.g.
  // ticking a module on, then saving Branding wipes the pending module
  // tick because the campaign object reference changed).
  useEffect(() => {
    if (!campaign) return
    setName(campaign.name || '')
    setSubtitle(campaign.subtitle || '')
    setLogoEmoji(campaign.logoEmoji || '')
    setDraftModules(Array.isArray(campaign.enabledModules) ? campaign.enabledModules : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.id])

  const fetchMembers = async () => {
    setLoadingMembers(true)
    setMembersError(null)
    try {
      const res = await fetch('/api/campaigns/members')
      if (res.ok) {
        setMembers(await res.json())
      } else {
        setMembersError('Failed to load members.')
      }
    } catch (err) {
      console.error('Failed to fetch members:', err)
      setMembersError('Failed to load members.')
    }
    setLoadingMembers(false)
  }

  // authEpoch: see the identical comment in components/tabs/ItemsTab.jsx.
  useEffect(() => {
    if (campaign && canEdit) fetchMembers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.id, canEdit, authEpoch])

  // /api/users is already gated server-side to site admins (403 for anyone
  // else), so this fetch is safe to skip entirely for a non-site-admin DM
  // rather than firing it and eating the 403.
  useEffect(() => {
    if (!isAdmin) return
    let cancelled = false
    fetch('/api/users')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!cancelled) setSiteUsers(Array.isArray(data) ? data : [])
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [isAdmin, authEpoch])

  if (loading) return <div className="cs-loading">Loading…</div>
  if (!campaign) return null // CampaignContext already redirects on an unknown slug
  if (!canEdit) return <Navigate to={`/c/${campaign.slug}/campaign`} replace />

  // ---- Branding ----

  const handleSaveBranding = async (e) => {
    e.preventDefault()
    setSavingBranding(true)
    try {
      const res = await fetch('/api/campaigns', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, subtitle, logo_emoji: logoEmoji }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        await showAlert({ title: 'Error', message: err.error || 'Failed to update campaign.', type: 'error' })
        return
      }
      await refreshCampaigns()
      await showAlert({ title: 'Saved', message: 'Campaign branding updated.', type: 'success' })
    } catch (err) {
      await showAlert({ title: 'Error', message: 'Failed to update campaign.', type: 'error' })
    } finally {
      setSavingBranding(false)
    }
  }

  // ---- Modules ----

  const handleToggleModule = (m) => {
    const isEnabled = draftModules.includes(m.id)
    if (isEnabled) {
      // Disabling hides the module's content rather than deleting it, so
      // warn before flipping the switch off.
      confirm({
        title: `Disable ${m.sidebarLabel}?`,
        message: 'Its content stays in the database and reappears if you re-enable it — it just disappears from the campaign for now.',
        confirmText: 'Disable',
        cancelText: 'Cancel',
        onConfirm: () => {
          setDraftModules((cur) => cur.filter((x) => x !== m.id))
        },
      })
    } else {
      setDraftModules((cur) => [...cur, m.id])
    }
  }

  const handleSaveModules = async () => {
    setSavingModules(true)
    try {
      const res = await fetch('/api/campaigns', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled_modules: draftModules }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        await showAlert({ title: 'Error', message: err.error || 'Failed to update modules.', type: 'error' })
        return
      }
      await refreshCampaigns()
      await showAlert({ title: 'Saved', message: 'Module selection updated.', type: 'success' })
    } catch (err) {
      await showAlert({ title: 'Error', message: 'Failed to update modules.', type: 'error' })
    } finally {
      setSavingModules(false)
    }
  }

  const modulesDirty = JSON.stringify([...draftModules].sort()) !==
    JSON.stringify([...(campaign.enabledModules || [])].sort())

  // ---- Map ----

  const handleMapFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setMapFile(file)
    // Clear any dimensions left over from a previously selected image so a
    // decode failure below can't leave stale, mismatched numbers sitting in
    // the fields (and the Upload button disabled per the guard below).
    setMapWidth('')
    setMapHeight('')
    const objectUrl = URL.createObjectURL(file)
    setMapPreviewUrl(objectUrl)
    const img = new Image()
    img.onload = () => {
      setMapWidth(String(img.naturalWidth))
      setMapHeight(String(img.naturalHeight))
    }
    img.onerror = () => {
      setMapWidth('')
      setMapHeight('')
    }
    img.src = objectUrl
  }

  const handleUploadMap = async (e) => {
    e.preventDefault()
    // Mirrors the button's disabled condition: guards the submit handler
    // itself (not just the button) so a missing file or blank/zero dimension
    // — e.g. from a cleared field, or a click that raced img.onload — can
    // never reach the server. upload-map.js coerces a blank string to 0 with
    // Number(''), which would silently persist map_width/height = 0 and
    // break every pin on the map.
    if (!mapFile || !mapWidth || !mapHeight || Number(mapWidth) <= 0 || Number(mapHeight) <= 0) return
    setUploadingMap(true)
    try {
      const form = new FormData()
      form.append('map', mapFile)
      form.append('map_width', mapWidth)
      form.append('map_height', mapHeight)
      const res = await fetch('/api/campaigns/upload-map', { method: 'POST', body: form })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        await showAlert({ title: 'Upload failed', message: err.error || 'Failed to upload map.', type: 'error' })
        return
      }
      await refreshCampaigns()
      setMapFile(null)
      setMapPreviewUrl(null)
      await showAlert({ title: 'Map uploaded', message: 'The campaign map has been updated.', type: 'success' })
    } catch (err) {
      await showAlert({ title: 'Upload failed', message: 'Failed to upload map.', type: 'error' })
    } finally {
      setUploadingMap(false)
    }
  }

  // ---- Members ----

  const handleAddMember = async (e) => {
    e.preventDefault()
    if (!newUsername.trim()) return
    setAddMemberError('')
    setAddingMember(true)
    try {
      const res = await fetch('/api/campaigns/members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: newUsername.trim(), role: newRole }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        // The server deliberately returns the same vague message whether the
        // username doesn't exist or is already a member — show it verbatim,
        // don't try to guess which case this is.
        setAddMemberError(err.error || 'Unable to add that user to this campaign')
        return
      }
      setNewUsername('')
      setNewRole('player')
      await fetchMembers()
      await refreshCampaigns()
    } catch (err) {
      setAddMemberError('Unable to add that user to this campaign')
    } finally {
      setAddingMember(false)
    }
  }

  const handleRemoveMember = (member) => {
    confirm({
      title: 'Remove member?',
      message: `Remove ${member.username} from this campaign? They will lose access immediately.`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      onConfirm: async () => {
        setRemovingUserId(member.userId)
        try {
          const res = await fetch('/api/campaigns/members', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: member.userId }),
          })
          if (!res.ok) {
            const err = await res.json().catch(() => ({}))
            await showAlert({ title: 'Error', message: err.error || 'Failed to remove member.', type: 'error' })
            return
          }
          await fetchMembers()
          await refreshCampaigns()
        } catch (err) {
          await showAlert({ title: 'Error', message: 'Failed to remove member.', type: 'error' })
        } finally {
          setRemovingUserId(null)
        }
      },
    })
  }

  const toggleableModules = MODULES.filter((m) => !ALWAYS_ON.includes(m.id))

  // Client-side filter for the site-admin-only autocomplete: substring match
  // against what's typed so far, minus anyone already a member of this
  // campaign. Never computed (or fetched — see the effect above) for a
  // non-site-admin DM.
  const existingUsernames = new Set(members.map((m) => m.username.toLowerCase()))
  const trimmedUsername = newUsername.trim().toLowerCase()
  const memberSuggestions = isAdmin && trimmedUsername
    ? siteUsers
        .filter(
          (u) =>
            u.username.toLowerCase().includes(trimmedUsername) &&
            !existingUsernames.has(u.username.toLowerCase())
        )
        .slice(0, 8)
    : []

  const handlePickSuggestion = (username) => {
    setNewUsername(username)
    setShowSuggestions(false)
  }

  // ---- Feyblood access ----

  const handleToggleFeybloodAccess = async (member) => {
    const next = !member.feybloodAccess
    setSavingAccessFor(member.userId)
    // Optimistic: the row flips immediately and is put back if the write
    // fails, so a DM never sees a toggle that claims a grant the server
    // refused.
    setMembers((prev) =>
      prev.map((m) => (m.userId === member.userId ? { ...m, feybloodAccess: next } : m))
    )
    try {
      const res = await fetch('/api/campaigns/members', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: member.userId, feyblood_access: next }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || 'Failed to update Feyblood access')
      }
    } catch (err) {
      setMembers((prev) =>
        prev.map((m) => (m.userId === member.userId ? { ...m, feybloodAccess: !next } : m))
      )
      showAlert({ title: 'Error', message: err.message, type: 'error' })
    } finally {
      setSavingAccessFor(null)
    }
  }

  return (
    <div className="cs-page-outer">
      <div className="cs-back-row">
        <button
          type="button"
          className="ss-btn ss-btn-neutral"
          onClick={() => navigate(`/c/${campaign.slug}/campaign`)}
        >
          ← Back to Campaign
        </button>
      </div>

      <SettingsShell
        title="Campaign Settings"
        subtitle={campaign.name}
        icon={campaign.logoEmoji || '🧭'}
        sections={sections}
        activeId={activeSection}
        onSelect={setActiveSection}
      >
        {activeSection === 'branding' && (
          <section>
            <h2>Branding</h2>
            <p className="ss-panel-hint">
              These appear in the top bar and browser tab for every player in this campaign.
            </p>
            <form onSubmit={handleSaveBranding} className="ss-form">
              <div className="ss-field">
                <label htmlFor="cs-name">Campaign name</label>
                <input
                  id="cs-name"
                  type="text"
                  className="ss-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="ss-field">
                <label htmlFor="cs-subtitle">Subtitle</label>
                <input
                  id="cs-subtitle"
                  type="text"
                  className="ss-input"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder="Optional tagline"
                />
              </div>
              <div className="ss-field cs-field-emoji">
                <label htmlFor="cs-emoji">Logo emoji</label>
                <input
                  id="cs-emoji"
                  type="text"
                  className="ss-input"
                  value={logoEmoji}
                  onChange={(e) => setLogoEmoji(e.target.value)}
                  maxLength={4}
                  placeholder="🧭"
                />
              </div>
              <button type="submit" className="ss-btn ss-btn-primary" disabled={savingBranding}>
                {savingBranding ? 'Saving…' : 'Save Branding'}
              </button>
            </form>
          </section>
        )}

        {activeSection === 'modules' && (
          <section>
            <h2>Modules</h2>
            <p className="ss-panel-hint">
              Choose which sections are active for this campaign. Overview is always on.
              Disabling a module hides its content — nothing is deleted.
            </p>
            <div className="cs-module-grid">
              {toggleableModules.map((m) => (
                <label key={m.id} className="cs-module-toggle">
                  <input
                    type="checkbox"
                    checked={draftModules.includes(m.id)}
                    onChange={() => handleToggleModule(m)}
                  />
                  <span className="cs-module-icon">{m.icon}</span>
                  <span className="cs-module-label">{m.sidebarLabel}</span>
                </label>
              ))}
            </div>
            <button
              type="button"
              className="ss-btn ss-btn-primary"
              onClick={handleSaveModules}
              disabled={savingModules || !modulesDirty}
            >
              {savingModules ? 'Saving…' : 'Save Modules'}
            </button>
          </section>
        )}

        {activeSection === 'map' && (
          <section>
            <h2>Campaign Map</h2>
            <p className="ss-panel-hint">
              Accepts JPEG, PNG, WebP, or GIF, up to 25MB. Dimensions matter — pins are
              stored as percentages of the map's width and height, so uploading a map
              with the wrong dimensions will misplace every existing pin. They're
              pre-filled from the image you choose; only change them if you know better.
            </p>

            {campaign.mapImageUrl && !mapPreviewUrl && (
              <div className="cs-map-current">
                <img src={campaign.mapImageUrl} alt="Current campaign map" />
                <span className="cs-map-dims">
                  Current: {campaign.mapWidth}×{campaign.mapHeight}px
                </span>
              </div>
            )}

            <form onSubmit={handleUploadMap} className="ss-form">
              <div className="ss-field">
                <label htmlFor="cs-map-file">Map image</label>
                <input
                  id="cs-map-file"
                  type="file"
                  accept="image/*"
                  className="ss-input-file"
                  onChange={handleMapFileChange}
                />
              </div>

              {mapPreviewUrl && (
                <div className="cs-map-current">
                  <img src={mapPreviewUrl} alt="New map preview" />
                </div>
              )}

              <div className="ss-field-row">
                <div className="ss-field">
                  <label htmlFor="cs-map-width">Width (px)</label>
                  <input
                    id="cs-map-width"
                    type="number"
                    className="ss-input"
                    value={mapWidth}
                    onChange={(e) => setMapWidth(e.target.value)}
                    min="1"
                  />
                </div>
                <div className="ss-field">
                  <label htmlFor="cs-map-height">Height (px)</label>
                  <input
                    id="cs-map-height"
                    type="number"
                    className="ss-input"
                    value={mapHeight}
                    onChange={(e) => setMapHeight(e.target.value)}
                    min="1"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="ss-btn ss-btn-primary"
                disabled={!mapFile || !mapWidth || !mapHeight || uploadingMap}
              >
                {uploadingMap ? 'Uploading…' : 'Upload Map'}
              </button>
            </form>
          </section>
        )}

        {activeSection === 'members' && (
          <section>
            <h2>Members</h2>
            <p className="ss-panel-hint">
              Players get a character sheet automatically when added. A campaign can have
              zero members — the site admin retains access to every campaign regardless.
            </p>

            {loadingMembers ? (
              <p className="cs-loading-text">Loading members…</p>
            ) : membersError ? (
              <p className="cs-error">{membersError}</p>
            ) : (
              <div className="cs-member-list">
                {members.map((m) => {
                  // A DM who isn't also a site admin must never be able to remove
                  // their own membership row: requireDM would deny them on their
                  // very next request and only the site admin could restore access.
                  // The 409 that used to block removing the last DM was dropped in
                  // this batch, so this is the only thing standing in the way now.
                  const isSelf = m.userId === user?.id
                  return (
                    <div key={m.userId} className="cs-member-item">
                      <span className="cs-member-username">{m.username}</span>
                      <span className="cs-member-role">{m.role === 'dm' ? 'DM' : 'Player'}</span>
                      <button
                        type="button"
                        className="ss-btn ss-btn-danger cs-member-remove"
                        onClick={() => handleRemoveMember(m)}
                        disabled={isSelf || removingUserId === m.userId}
                        title={isSelf ? "You can't remove yourself from a campaign." : undefined}
                      >
                        {removingUserId === m.userId ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  )
                })}
                {members.length === 0 && <p className="cs-loading-text">No members yet.</p>}
              </div>
            )}

            <form onSubmit={handleAddMember} className="ss-form cs-add-member-form">
              <div className="ss-field-row">
                <div className="ss-field cs-autocomplete-wrap">
                  <label htmlFor="cs-new-username">Username</label>
                  <input
                    id="cs-new-username"
                    type="text"
                    className="ss-input"
                    value={newUsername}
                    onChange={(e) => {
                      setNewUsername(e.target.value)
                      setShowSuggestions(true)
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    placeholder="Existing account username"
                    autoComplete="off"
                    required
                  />
                  {isAdmin && showSuggestions && memberSuggestions.length > 0 && (
                    <ul className="cs-autocomplete-list">
                      {memberSuggestions.map((u) => (
                        <li key={u.id}>
                          <button
                            type="button"
                            className="cs-autocomplete-option"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handlePickSuggestion(u.username)}
                          >
                            <span className="cs-autocomplete-username">{u.username}</span>
                            <span className="cs-autocomplete-role">
                              {u.role === 'admin' ? 'Site Admin' : 'Player'}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="ss-field cs-field-role">
                  <label htmlFor="cs-new-role">Role</label>
                  <select
                    id="cs-new-role"
                    className="ss-select"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                  >
                    <option value="player">Player</option>
                    <option value="dm">DM</option>
                  </select>
                </div>
              </div>

              {addMemberError && <p className="cs-error">{addMemberError}</p>}

              <button type="submit" className="ss-btn ss-btn-primary" disabled={addingMember}>
                {addingMember ? 'Adding…' : 'Add Member'}
              </button>
            </form>
          </section>
        )}

        {activeSection === 'feyblood' && feybloodEnabled && (
          <section>
            <h2>Feyblood Access</h2>
            <p className="ss-panel-hint">
              Choose who can reach the Feyblood page and roll the d100. Players without
              access see the button greyed out. You and any site admin always have access.
            </p>

            {loadingMembers ? (
              <p className="cs-loading-text">Loading members…</p>
            ) : membersError ? (
              <p className="cs-error">{membersError}</p>
            ) : members.length === 0 ? (
              <p className="cs-loading-text">
                No members yet — add players in the Members section first.
              </p>
            ) : (
              <div className="cs-member-list">
                {members.map((m) => {
                  // A DM bypasses the grant column entirely (server-side, via
                  // canEdit), so offering a toggle that can't change anything
                  // would just be a lie about what the row does.
                  const isDm = m.role === 'dm'
                  const granted = isDm || m.feybloodAccess
                  return (
                    <div key={m.userId} className="cs-member-item">
                      <span className="cs-member-username">{m.username}</span>
                      <span className="cs-member-role">{isDm ? 'DM' : 'Player'}</span>
                      <button
                        type="button"
                        className={`ss-btn cs-access-toggle ${granted ? 'ss-btn-primary' : 'ss-btn-neutral'}`}
                        onClick={() => handleToggleFeybloodAccess(m)}
                        disabled={isDm || savingAccessFor === m.userId}
                        title={isDm ? 'DMs always have Feyblood access.' : undefined}
                      >
                        {isDm
                          ? 'Always'
                          : savingAccessFor === m.userId
                            ? 'Saving…'
                            : granted
                              ? '✓ Granted'
                              : 'No access'}
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </section>
        )}
      </SettingsShell>
    </div>
  )
}

export default CampaignSettingsPage
